import { useCallback, useState } from "react";
import { useMutation } from "urql";
import { graphql } from "@acto/gql";
import { CreateTodoInput } from "@acto/gql/graphql";

// Selects the same node fields as `ClientTodosForPage` so the normalized `Todo:<id>` entity
// graphcache writes here is reused by the customer TodosPage buckets after creation.
const CreateTodoMutation = graphql(`
  mutation CreateTodo($input: CreateTodoInput!) {
    createTodo(input: $input) {
      id
      title
      description
      dueDate
      processedStatus
      processedReason
      isWorkflow
      isNoteBased
      __typename
      assignedEmployee {
        id
        name
        __typename
      }
      assignedUser {
        id
        name
        __typename
        employee {
          id
          name
          __typename
        }
      }
      client {
        id
        legacyId
        name
        externalReference
        __typename
      }
    }
  }
`);

export function useCreateTodo() {
  const [result, createTodoMutation] = useMutation(CreateTodoMutation);
  const [error, setError] = useState<Error | null>(null);

  const createTodo = useCallback(
    async (input: CreateTodoInput) => {
      try {
        const response = await createTodoMutation({ input });
        if (response.error) {
          throw response.error;
        }
        return response.data?.createTodo;
      } catch (err: unknown) {
        if (err instanceof Error) {
          setError(err);
          return null;
        } else {
          throw err;
        }
      }
    },
    [createTodoMutation],
  );

  return { createTodo, result, error };
}
